import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { serverApi } from '../../../api';

const MySwal = withReactContent(Swal);

const inputStyle = {
    width: '100%', padding: '11px 12px', borderRadius: 10,
    border: '1.5px solid #cbd5e1', marginTop: 4,
    boxSizing: 'border-box', fontSize: 14, background: 'white',
    outline: 'none', color: '#0f172a',
};

const labelStyle = { fontSize: 12, fontWeight: 700, color: '#475569' };

const botonAccion = (bg, color) => ({
    width: 32, height: 32, borderRadius: 9, border: 'none',
    background: bg, color, cursor: 'pointer', fontSize: 13,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background .15s', flexShrink: 0,
});

export const GestionarModuloClasesModal = ({ show, onClose, onChange }) => {
    const [clases, setClases] = useState([]);
    const [nombreClase, setNombreClase] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingUid, setEditingUid] = useState(null);
    const [editingNombre, setEditingNombre] = useState('');

    const cargarClases = useCallback(async () => {
        const codigoEntidad = localStorage.getItem('codigoEntidad');
        if (!codigoEntidad) return;

        try {
            setLoading(true);
            const resp = await serverApi.post('/modulos/lista-moduloClases', { codigoEntidad });
            if (resp.data.ok) {
                setClases(resp.data.clases || []);
            }
        } catch (error) {
            console.error('Error al cargar clases de módulo:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (show) {
            cargarClases();
            setNombreClase('');
            setEditingUid(null);
        }
    }, [show, cargarClases]);

    const handleCrearClase = async (e) => {
        e.preventDefault();
        if (!nombreClase.trim()) {
            return MySwal.fire('Atención', 'Ingrese el nombre de la clase de módulo', 'warning');
        }

        const codigoEntidad = localStorage.getItem('codigoEntidad');
        try {
            setLoading(true);
            const resp = await serverApi.post('/modulos/registra-moduloClase', {
                codigoEntidad,
                moduloClase: nombreClase.trim(),
            });

            if (resp.data.ok) {
                MySwal.fire({
                    title: '¡Clase creada!',
                    text: resp.data.resp,
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                });
                setNombreClase('');
                cargarClases();
                if (onChange) onChange();
            } else {
                MySwal.fire('Atención', resp.data.resp || 'No se pudo crear la clase', 'error');
            }
        } catch (error) {
            MySwal.fire('Error', 'Error al crear la clase de módulo', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGuardarEdicion = async (uidModuloClase) => {
        if (!editingNombre.trim()) return setEditingUid(null);

        const codigoEntidad = localStorage.getItem('codigoEntidad');
        try {
            setLoading(true);
            const resp = await serverApi.post('/modulos/edita-moduloClase', {
                codigoEntidad,
                uidModuloClase,
                moduloClase: editingNombre.trim(),
            });

            if (resp.data.ok) {
                MySwal.fire({
                    title: '¡Actualizado!',
                    text: resp.data.resp,
                    icon: 'success',
                    timer: 1200,
                    showConfirmButton: false,
                });
                setEditingUid(null);
                cargarClases();
                if (onChange) onChange();
            } else {
                MySwal.fire('Atención', resp.data.resp, 'error');
            }
        } catch (error) {
            MySwal.fire('Error', 'Error al editar la clase de módulo', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEstado = async (uidModuloClase, estadoActual) => {
        const codigoEntidad = localStorage.getItem('codigoEntidad');
        const nuevoEstado = estadoActual === 1 ? 0 : 1;
        const accion = nuevoEstado === 1 ? 'activar' : 'inactivar';

        const result = await MySwal.fire({
            title: '¿Está seguro?',
            text: `¿Desea ${accion} esta clase de módulo?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, continuar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: 'var(--color-primario)',
        });

        if (!result.isConfirmed) return;

        try {
            setLoading(true);
            const resp = await serverApi.post('/modulos/inactiva-moduloClase', {
                codigoEntidad,
                uidModuloClase,
                accion: nuevoEstado,
            });

            if (resp.data.ok) {
                cargarClases();
                if (onChange) onChange();
            }
        } catch (error) {
            MySwal.fire('Error', 'Error al cambiar estado de la clase', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 20, fontFamily: "'Inter', Arial, sans-serif",
        }}>
            <div style={{
                background: '#ffffff', borderRadius: 24, width: '100%',
                maxWidth: 720, maxHeight: '92vh', overflowY: 'auto',
                boxShadow: '0 25px 60px rgba(0,0,0,0.25)', border: '1px solid #e2e8f0',
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-oscuro) 100%)',
                    padding: '20px 26px', color: 'var(--color-primario-contraste)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    position: 'sticky', top: 0, zIndex: 1,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{
                            width: 44, height: 44, borderRadius: 14,
                            background: 'rgba(255,255,255,0.18)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 20,
                        }}>
                            <i className="fa-solid fa-tags"></i>
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontWeight: 800, fontSize: 18 }}>
                                Clases de módulo
                            </h4>
                            <p style={{ margin: '3px 0 0', opacity: 0.85, fontSize: 12.5 }}>
                                Crea, edita y activa / inactiva las clases disponibles
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        background: 'rgba(255,255,255,0.15)', border: 'none', color: 'var(--color-primario-contraste)',
                        width: 34, height: 34, borderRadius: '50%', fontSize: 16, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}><i className="fa-solid fa-xmark"></i></button>
                </div>

                <div style={{ padding: 26 }}>

                    {/* Formulario de creación de clase */}
                    <form onSubmit={handleCrearClase} style={{
                        marginBottom: 22, padding: 16, borderRadius: 14,
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                    }}>
                        <label style={labelStyle}>Nueva clase de módulo</label>
                        <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="Ej: TAQUILLA, CONSULTORIO, VENTANILLA..."
                                value={nombreClase}
                                onChange={(e) => setNombreClase(e.target.value.toUpperCase())}
                                style={{ ...inputStyle, flex: 1, minWidth: 200, marginTop: 0, textTransform: 'uppercase' }}
                            />
                            <button
                                type="submit"
                                disabled={loading || !nombreClase.trim()}
                                style={{
                                    padding: '11px 22px', borderRadius: 10, border: 'none',
                                    background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-secundario) 100%)',
                                    color: 'var(--color-marca-contraste)', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 4px 14px rgba(var(--color-primario-rgb),0.3)',
                                    opacity: loading || !nombreClase.trim() ? 0.6 : 1,
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                }}
                            >
                                <i className="fa-solid fa-plus"></i> Crear clase
                            </button>
                        </div>
                    </form>

                    {/* Lista de clases */}
                    <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 2 }}>
                        {clases.length === 0 ? (
                            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                                <div style={{ color: '#94a3b8', fontSize: 30, marginBottom: 8 }}>
                                    <i className="fa-solid fa-tags"></i>
                                </div>
                                <div style={{ fontWeight: 700, color: '#64748b', fontSize: 14 }}>
                                    No hay clases de módulo registradas
                                </div>
                                <div style={{ color: '#94a3b8', fontSize: 12.5, marginTop: 4 }}>
                                    Crea la primera clase con el formulario de arriba.
                                </div>
                            </div>
                        ) : (
                            clases.map((item) => {
                                const activo = item.indActivo === 1;
                                return (
                                    <div
                                        key={item.uidModuloClase}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 12,
                                            background: 'white', borderRadius: 12,
                                            border: `1.5px solid ${activo ? '#e2e8f0' : '#fecaca'}`,
                                            padding: '12px 14px', marginBottom: 10,
                                            opacity: activo ? 1 : 0.75,
                                        }}
                                    >
                                        <button
                                            type="button"
                                            style={botonAccion(activo ? '#fee2e2' : '#d1fae5', activo ? '#b91c1c' : '#047857')}
                                            onClick={() => handleToggleEstado(item.uidModuloClase, item.indActivo)}
                                            title={activo ? 'Inactivar' : 'Activar'}
                                        >
                                            <i className={`fa-solid ${activo ? 'fa-ban' : 'fa-check'}`}></i>
                                        </button>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            {editingUid === item.uidModuloClase ? (
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    value={editingNombre}
                                                    onChange={(e) => setEditingNombre(e.target.value.toUpperCase())}
                                                    onBlur={() => handleGuardarEdicion(item.uidModuloClase)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleGuardarEdicion(item.uidModuloClase);
                                                        if (e.key === 'Escape') setEditingUid(null);
                                                    }}
                                                    style={{
                                                        width: '100%', padding: '6px 10px', borderRadius: 8,
                                                        border: '1.5px solid var(--color-primario)', fontSize: 14, outline: 'none',
                                                        fontWeight: 800, textTransform: 'uppercase',
                                                    }}
                                                />
                                            ) : (
                                                <span style={{
                                                    fontWeight: 800,
                                                    color: activo ? '#0f172a' : '#94a3b8',
                                                    textDecoration: activo ? 'none' : 'line-through',
                                                }}>
                                                    {item.moduloClase.toUpperCase()}
                                                </span>
                                            )}
                                        </div>

                                        {!activo && (
                                            <span style={{
                                                background: '#fee2e2', color: '#b91c1c', fontWeight: 800,
                                                padding: '3px 10px', borderRadius: 999, fontSize: 11,
                                            }}>
                                                INACTIVA
                                            </span>
                                        )}

                                        <button
                                            type="button"
                                            title="Editar"
                                            style={botonAccion('var(--color-secundario-suave)', 'var(--color-primario)')}
                                            onClick={() => {
                                                setEditingUid(item.uidModuloClase);
                                                setEditingNombre(item.moduloClase);
                                            }}
                                        >
                                            <i className="fa-solid fa-pen"></i>
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex', justifyContent: 'flex-end',
                    padding: '16px 26px', borderTop: '1px solid #f1f5f9', background: '#f8fafc',
                }}>
                    <button type="button" onClick={onClose} style={{
                        padding: '12px 22px', borderRadius: 12, border: '1.5px solid #cbd5e1',
                        background: 'white', color: '#475569', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                    }}>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
